import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { tokenManager } from '../services/authApi';
import { reportApi, GenerateReportRequest } from '../services/reportApi';
import { templateApi } from '../services/templateApi';
import { caseApiReal } from '../services/caseApiReal';

export const DebugAuthPage: React.FC = () => {
  const { user, isAuthenticated, token } = useAuth();
  const [testResult, setTestResult] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const checkAuth = () => {
    const storedToken = tokenManager.getAccessToken();
    const storedUser = tokenManager.getStoredUser();
    
    let result = '=== AUTHENTICATION DEBUG ===\n\n';
    result += `Auth Context:\n`;
    result += `- isAuthenticated: ${isAuthenticated}\n`;
    result += `- user: ${user ? JSON.stringify(user, null, 2) : 'null'}\n`;
    result += `- token from context: ${token ? 'EXISTS' : 'NULL'}\n\n`;
    
    result += `LocalStorage:\n`;
    result += `- clinlexis_token: ${storedToken ? 'EXISTS' : 'NULL'}\n`;
    result += `- Token expired: ${storedToken ? tokenManager.isTokenExpired(storedToken) : 'N/A'}\n`;
    result += `- Stored user: ${storedUser ? JSON.stringify(storedUser, null, 2) : 'null'}\n\n`;
    
    if (storedToken) {
      try {
        const payload = JSON.parse(atob(storedToken.split('.')[1]));
        result += `Token Payload:\n`;
        result += `- sub (user ID): ${payload.sub}\n`;
        result += `- email: ${payload.email}\n`;
        result += `- exp: ${new Date(payload.exp * 1000).toISOString()}\n`;
        result += `- iat: ${new Date(payload.iat * 1000).toISOString()}\n`;
        result += `- Full payload: ${JSON.stringify(payload, null, 2)}\n\n`;
      } catch (e) {
        result += `Token Payload: ERROR parsing token\n\n`;
      }
    }
    
    setTestResult(result);
  };

  const testTemplateApi = async () => {
    setIsLoading(true);
    let result = '=== TEMPLATE API TEST ===\n\n';
    
    try {
      result += 'Calling templateApi.getTemplates()...\n';
      const templates = await templateApi.getTemplates();
      result += `✅ SUCCESS: Got ${templates.length} templates\n`;
      result += `Templates: ${JSON.stringify(templates.map(t => ({ id: t.id, name: t.name })), null, 2)}\n`;
    } catch (error: any) {
      result += `❌ ERROR: ${error.message}\n`;
      result += `Details: ${JSON.stringify(error, null, 2)}\n`;
    }
    
    setTestResult(result);
    setIsLoading(false);
  };

  const testCaseApi = async () => {
    setIsLoading(true);
    let result = '=== CASE API TEST ===\n\n';
    
    try {
      result += 'Calling caseApiReal.getCases()...\n';
      const response = await caseApiReal.getCases({ size: 10 });
      result += `✅ SUCCESS: Got ${response.content.length} cases\n`;
      result += `Cases: ${JSON.stringify(response.content.map(c => ({ id: c.id, name: c.name })), null, 2)}\n`;
    } catch (error: any) {
      result += `❌ ERROR: ${error.message}\n`;
      result += `Details: ${JSON.stringify(error, null, 2)}\n`;
    }
    
    setTestResult(result);
    setIsLoading(false);
  };

  const testReportGeneration = async () => {
    setIsLoading(true);
    let result = '=== REPORT GENERATION TEST ===\n\n';
    
    try {
      // First get a case and template
      result += 'Step 1: Getting cases...\n';
      const casesResponse = await caseApiReal.getCases({ size: 1 });
      if (casesResponse.content.length === 0) {
        result += '❌ ERROR: No cases found\n';
        setTestResult(result);
        setIsLoading(false);
        return;
      }
      const caseId = casesResponse.content[0].id;
      result += `✅ Got case: ${caseId}\n\n`;
      
      result += 'Step 2: Getting templates...\n';
      const templates = await templateApi.getTemplates();
      if (templates.length === 0) {
        result += '❌ ERROR: No templates found\n';
        setTestResult(result);
        setIsLoading(false);
        return;
      }
      const templateId = templates[0].id;
      result += `✅ Got template: ${templateId}\n\n`;
      
      result += 'Step 3: Generating report...\n';
      const request: GenerateReportRequest = {
        caseId,
        templateId,
        title: 'Debug Test Report',
        userPrompt: 'This is a test'
      };
      result += `Request: ${JSON.stringify(request, null, 2)}\n\n`;
      
      const report = await reportApi.generateReport(request);
      result += `✅ SUCCESS: Report generated!\n`;
      result += `Report ID: ${report.reportId}\n`;
      result += `Report: ${JSON.stringify(report, null, 2)}\n`;
    } catch (error: any) {
      result += `❌ ERROR: ${error.message}\n`;
      result += `Error code: ${error.code}\n`;
      result += `Details: ${JSON.stringify(error, null, 2)}\n`;
      
      // Check if it's a 403
      if (error.message?.includes('403') || error.code?.includes('403')) {
        result += `\n🔍 403 FORBIDDEN - Possible causes:\n`;
        result += `1. JWT token not being sent in Authorization header\n`;
        result += `2. JWT token is expired\n`;
        result += `3. Backend security config rejecting the request\n`;
        result += `4. User doesn't have permission\n\n`;
        result += `Check browser Network tab for the actual request headers!\n`;
      }
    }
    
    setTestResult(result);
    setIsLoading(false);
  };

  const clearAuth = () => {
    tokenManager.clearTokens();
    setTestResult('✅ Cleared all tokens. Refresh the page to log out.');
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-medical-900 mb-6">Authentication Debug Page</h1>
      
      <div className="space-y-4 mb-6">
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Quick Status</h2>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${isAuthenticated ? 'bg-green-500' : 'bg-red-500'}`} />
              <span>Authentication: {isAuthenticated ? 'Logged In' : 'Not Logged In'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${token ? 'bg-green-500' : 'bg-red-500'}`} />
              <span>Token: {token ? 'Present' : 'Missing'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${user ? 'bg-green-500' : 'bg-red-500'}`} />
              <span>User: {user ? user.email : 'None'}</span>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Debug Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={checkAuth} className="btn-secondary" disabled={isLoading}>
              Check Auth Status
            </button>
            <button onClick={testTemplateApi} className="btn-secondary" disabled={isLoading}>
              Test Template API
            </button>
            <button onClick={testCaseApi} className="btn-secondary" disabled={isLoading}>
              Test Case API
            </button>
            <button onClick={testReportGeneration} className="btn-primary" disabled={isLoading}>
              Test Report Generation
            </button>
            <button onClick={clearAuth} className="btn-secondary text-red-600" disabled={isLoading}>
              Clear Auth Tokens
            </button>
          </div>
        </div>

        {testResult && (
          <div className="card bg-gray-50">
            <h2 className="text-xl font-semibold mb-4">Test Results</h2>
            <pre className="text-xs font-mono whitespace-pre-wrap overflow-x-auto bg-white p-4 rounded border">
              {testResult}
            </pre>
          </div>
        )}
      </div>

      <div className="card bg-cyan-50 border-cyan-200">
        <h3 className="font-semibold text-cyan-900 mb-2">How to Use This Page</h3>
        <ol className="text-sm text-cyan-800 space-y-1 list-decimal list-inside">
          <li>Click "Check Auth Status" to see your authentication state</li>
          <li>Click "Test Template API" to verify template fetching works</li>
          <li>Click "Test Case API" to verify case fetching works</li>
          <li>Click "Test Report Generation" to reproduce the 403 error</li>
          <li>Open browser DevTools (F12) → Network tab to see actual requests</li>
          <li>Look for the Authorization header in the request</li>
        </ol>
      </div>
    </div>
  );
};
